import {
  createTimeseriesProvider,
  type TimeseriesNative,
  type TimeseriesParams,
} from '#core/lib/expr/providers/timeseries.ts';
import type { DatasetProvider } from '#core/lib/expr/providers/types.ts';

import { useBatchState } from '#plugin-chat/lib/source-fetchers/useBatchState.ts';
import { useDatapointsFetch } from '#plugin-chat/lib/source-fetchers/useDatapointsFetch.ts';

/**
 * The per-conversation timeseries provider, assembled from the chat-side
 * datapoints-fetch seam (`useDatapointsFetch`) and the pinned default window
 * (`useBatchState`). The same instance both registers under the timeseries
 * group for top-level `(datapoints)` queries and mints per-row view cells via
 * `buildSource` — so `ChatSessionProvider` and the iPSO catalog hooks share one
 * construction path. Coalescing lives in the per-`QueryClient` `BatchState`, so
 * re-deriving the provider per render (or per catalog hook) never resets it.
 */
export function useSessionTimeseries(): DatasetProvider<TimeseriesParams, TimeseriesNative> {
  const { defaultStart, defaultEnd } = useBatchState();
  const provider = createTimeseriesProvider({
    fetchDatapoints: useDatapointsFetch(),
    defaultWindow: [new Date(defaultStart).toISOString(), new Date(defaultEnd).toISOString()],
  });
  if (!provider.buildSource) {
    throw new Error('timeseries provider missing buildSource');
  }
  return provider;
}
